import type {
  AddressGroupFilters as Filters,
  SubclusterStatus,
} from '../types';
import { STATUS_META, SUBCLUSTER_STATUSES } from '../status-vocabulary';

// The run opens unfiltered: every group is in view until a reviewer narrows it.
export const DEFAULT_ADDRESS_FILTERS: Filters = {
  statuses: [],
  matchedOn: [],
  variations: [null, null],
  docs: [null, null],
};

export type Range = [number | null, number | null];

// Filter labels come from the one status vocabulary (Title Case on both run
// types); only which statuses show pre- vs post-execution lives here.
/**
 * A status as a filter option.
 * @param {SubclusterStatus} value The status.
 * @return {object} Its option.
 */
function statusOption(value: SubclusterStatus) {
  return { value, label: STATUS_META[value].filter };
}

const PRE_STATUS_OPTIONS = (
  ['in_scope', 'partially_excluded', 'excluded'] as const
).map(statusOption);

// An executed run can still hold executable groups a timed-out batch never
// reached, so keep those filterable alongside the outcomes — the reviewer needs
// to see (and re-run) the remainder. The display order is curated, but the list
// is reconciled against the one status vocabulary: a status the order missed
// (e.g. a newly added Ruby status) is appended rather than silently dropped.
const POST_STATUS_ORDER: readonly SubclusterStatus[] = [
  'in_scope',
  'partially_excluded',
  'combined',
  'failed',
  'reversed',
  'partially_reversed',
  'excluded',
];
const POST_STATUS_OPTIONS = [
  ...POST_STATUS_ORDER,
  ...SUBCLUSTER_STATUSES.filter((s) => !POST_STATUS_ORDER.includes(s)),
].map(statusOption);

/**
 * The status filter's options: the outcomes too once the run has executed.
 * @param {boolean} executed Whether the run has executed.
 * @return {Array} The options.
 */
export function statusOptions(executed: boolean) {
  return executed ? POST_STATUS_OPTIONS : PRE_STATUS_OPTIONS;
}

/**
 * Whether a count range has either bound set.
 * @param {Range} range The range.
 * @return {boolean} Whether it narrows anything.
 */
export function rangeSet([min, max]: Range) {
  return min !== null || max !== null;
}

/**
 * How many filters are applied, a range counting once.
 * @param {Filters} filters The filters.
 * @return {number} The count.
 */
export function activeFilterCount(filters: Filters): number {
  return (
    filters.statuses.length +
    filters.matchedOn.length +
    (rangeSet(filters.variations) ? 1 : 0) +
    (rangeSet(filters.docs) ? 1 : 0)
  );
}

/**
 * Query params for the subclusters request; an empty filter sends nothing.
 * @param {Filters} filters The filters.
 * @return {string} The query string.
 */
export function filterQuery(filters: Filters): string {
  const params = new URLSearchParams();
  filters.statuses.forEach((s) => params.append('status[]', s));
  filters.matchedOn.forEach((m) => params.append('matchedOn[]', m));
  const bounds: [string, number | null][] = [
    ['variationsMin', filters.variations[0]],
    ['variationsMax', filters.variations[1]],
    ['docsMin', filters.docs[0]],
    ['docsMax', filters.docs[1]],
  ];
  bounds.forEach(([key, value]) => {
    if (value !== null) params.set(key, String(value));
  });
  return params.toString();
}
